import h from 'hyperscript';

import { assertType } from '@disy/lib/custom-error';
import { uniqueId } from '@disy/lib/unique-id';
import type { Content } from '@disy/ui/content';
import type { LabelWithIcon } from '@disy/ui/label-with-icon';

type ItemRow<T> = HTMLTableRowElement & { dRowData: T };
type GroupRow = HTMLTableRowElement & { dGroupData: unknown };

export function getRowData<T>(tr: HTMLTableRowElement) {
  return (tr as ItemRow<T>).dRowData;
}

export function getGroup(tr: HTMLTableRowElement) {
  return (tr as GroupRow).dGroupData;
}

export function isGroup(tr: HTMLTableRowElement) {
  return getRowData<unknown>(tr) === undefined;
}

export function attachItemData<T>(tr: HTMLTableRowElement, item: T) {
  (tr as ItemRow<T>).dRowData = item;
}

export function attachGroupData(tr: HTMLTableRowElement, group: unknown) {
  (tr as GroupRow).dGroupData = group;
}

export function asRow(element: Element) {
  assertType<HTMLTableRowElement>(element, element instanceof HTMLTableRowElement);
  return element;
}

export function getPreviousRow(row: HTMLTableRowElement) {
  const previous = row.previousElementSibling;
  return previous ? asRow(previous) : undefined;
}

export function getNextRow(row: HTMLTableRowElement) {
  const next = row.nextElementSibling;
  return next ? asRow(next) : undefined;
}

export function createLabelWithDetail(label: LabelWithIcon, detail: Content): Element {
  const COMPONENT_NAME = 'd-label-with-detail';
  const detailElement = h(
    `span.${COMPONENT_NAME}--detail`,
    {
      id: uniqueId(),
      attrs: { role: 'note' },
    },
    detail,
  );
  label.setAttribute('aria-describedby', detailElement.id);
  return h(`.${COMPONENT_NAME}.d-stack-v.space-0.5`, [label, detailElement]);
}

export function toggleDarkLight(el: HTMLElement, dark: boolean) {
  el.classList.toggle('dark-bg', dark);
  el.classList.toggle('light-bg', !dark);
}
